/**
 * The client panel's step-3 sections (prototype Client 360 `VIEWS.client` 1262, `clientItems` 1377):
 *  - "To do for this client" (Today and overdue): open escalations, client tasks due within 2 days, client
 *    requests with 24 hours or less left (or past time), and the client's tasks due today or overdue.
 *  - "Client tasks due": every open client task with its due chip.
 *
 * Data: one GET /work?client_id=&status=open, which the server already scopes to the caller (a SPOC sees their
 * own work, as the prototype's `mine` filter does for non-managers). Clicking a row opens it by its `route`.
 */
import { useNavigate } from 'react-router';
import type { WorkItem } from '@amplispot/contracts';
import { firstName, todayIso } from '../../lib/format';
import { shortDay } from '../../lib/requests';
import { daysBetween, dueText, useAllWork } from '../../lib/work';
import { Empty, ErrorState, Loading } from '../../ui/kit';

interface Row {
  id: string;
  kd: string;
  rank: number;
  title: string;
  meta: string;
  chip: { tone: string; label: string };
  route: string;
}

const useClientWork = (clientId: string) => useAllWork({ client_id: clientId, status: 'open' });

/** Prototype `clientItems`: what needs doing for this client today, most urgent first. */
export function toDoRows(items: WorkItem[], today = todayIso()): Row[] {
  const out: Row[] = [];
  const who = (i: WorkItem) => (i.assignee ? firstName(i.assignee.name) : 'Not assigned');
  for (const i of items) {
    if (i.kind === 'escalation' && i.status === 'open') {
      out.push({ id: i.id, kd: 'Escalation', rank: i.severity === 'high' ? 0 : 1, title: i.title, meta: `Owner ${who(i)}`, chip: { tone: 'cold', label: `Open since ${shortDay(i.opened_on)}` }, route: i.route });
    } else if (i.kind === 'client_task' && i.status === 'open' && i.due && daysBetween(today, i.due) <= 2) {
      const d = daysBetween(today, i.due);
      out.push({ id: i.id, kd: 'Task', rank: d < 0 ? 0 : 3, title: i.title, meta: `Owed to ${i.promised_to} by ${who(i)}`, chip: dueText(i.due, today), route: i.route });
    } else if (i.kind === 'client_request' && i.status !== 'done' && i.hours_left <= 24) {
      const left = Math.round(i.hours_left);
      out.push({
        id: i.id,
        kd: 'Request',
        rank: left < 0 ? 1 : 4,
        title: i.title,
        meta: `Via ${i.via}${i.assignee ? `, ${firstName(i.assignee.name)}` : ''}`,
        chip: left < 0 ? { tone: 'cold', label: `${-left}h past time` } : { tone: 'warm', label: `${left}h left` },
        route: i.route,
      });
    } else if (i.kind === 'task' && !i.done && i.due && daysBetween(today, i.due) <= 0) {
      const blocked = i.badges.find((b) => b.key === 'blocked');
      out.push({ id: i.id, kd: 'Task', rank: daysBetween(today, i.due) < 0 ? 1 : 4, title: i.title, meta: `${who(i)}${blocked ? `, ${blocked.label.toLowerCase()}` : ''}`, chip: dueText(i.due, today), route: i.route });
    }
  }
  return out.sort((a, b) => a.rank - b.rank);
}

export function ClientToDo({ clientId }: { clientId: string }) {
  const nav = useNavigate();
  const q = useClientWork(clientId);
  const rows = toDoRows(q.data ?? []);
  return (
    <>
      <h3 className="split" style={{ justifyContent: 'space-between' }}>
        <span>To do for this client</span>
        <span className="sub" style={{ fontSize: 12, fontWeight: 400 }}>
          Today and overdue
        </span>
      </h3>
      <div className="panel" style={{ boxShadow: 'none' }}>
        {q.isLoading ? (
          <Loading />
        ) : q.error ? (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        ) : rows.length ? (
          rows.map((r) => (
            <div key={r.id} className="it" style={{ paddingLeft: 14 }} role="button" tabIndex={0} onClick={() => nav(r.route)} onKeyDown={(e) => e.key === 'Enter' && nav(r.route)}>
              <span className="kd">{r.kd}</span>
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="tt">{r.title}</div>
                <div className="mm">{r.meta}</div>
              </div>
              <span className={`chip ${r.chip.tone}`}>{r.chip.label}</span>
            </div>
          ))
        ) : (
          <Empty title="Nothing due">Everything for this client is on track.</Empty>
        )}
      </div>
    </>
  );
}

export function ClientTasksDue({ clientId }: { clientId: string }) {
  const nav = useNavigate();
  const q = useClientWork(clientId);
  const today = todayIso();
  const open = (q.data ?? []).filter((i): i is Extract<WorkItem, { kind: 'client_task' }> => i.kind === 'client_task' && i.status === 'open');
  return (
    <>
      <h3>Client tasks due</h3>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : open.length ? (
        <div className="panel" style={{ boxShadow: 'none' }}>
          {open.map((m) => {
            const chip = m.due ? dueText(m.due, today) : { tone: '', label: 'No date' };
            return (
              <div key={m.id} className="it" style={{ paddingLeft: 14 }} role="button" tabIndex={0} onClick={() => nav(m.route)} onKeyDown={(e) => e.key === 'Enter' && nav(m.route)}>
                <div className="grow tt" style={{ minWidth: 0 }}>
                  {m.title}
                </div>
                <span className={`chip ${chip.tone}`}>{chip.label}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="sub">No open client tasks</div>
      )}
    </>
  );
}
